import { create } from 'zustand';
import type {
  EditorMetadata,
  EditorResponse,
  GroupData,
  SectionGroup,
  SectionMetadata,
  SectionSchema,
} from '../api/types';
import {
  fetchEditorPage,
  renderSection,
  saveSectionSettings,
  saveSectionOrder,
  addSection as apiAddSection,
  removeSection as apiRemoveSection,
  saveGlobalSettings,
  syncThemeFromDefault,
  readThemeFile,
  type EditorSession,
  type ThemeSyncReport,
} from '../api/client';
import { debounce } from '../lib/debounce';
import {
  discoverCssVarsFromLiquid,
  setDiscoveredCssVars,
  discoverSectionCssVarsFromLiquid,
  setDiscoveredSectionCssVars,
  type SectionCssVarBinding,
} from '../preview/css-var-patcher';

export type Selection =
  | { kind: 'section'; group: SectionGroup; sectionId: string }
  | { kind: 'block'; group: SectionGroup; sectionId: string; blockId: string }
  | { kind: 'global'; groupFilter?: string }
  | null;

export type Device = 'desktop' | 'mobile';
export type Mode = 'visual' | 'code';

export interface SaveStatus {
  state: 'idle' | 'saving' | 'saved' | 'error';
  message?: string;
  lastSavedAt?: number;
  progress?: { current: number; total: number };
}

/** Per-page editable state (sections + order). */
export interface PageSlice {
  sections: Record<string, SectionMetadata>;
  order: string[];
  initialHtml: string;
  metadata: EditorMetadata;
}

/** Server-truth baselines, kept separate so we can diff precisely on save. */
interface PageBaseline {
  sections: Record<string, SectionMetadata>;
  order: string[];
}
interface ThemeBaseline {
  globalSettings: Record<string, any>;
  headerGroup: GroupData | null;
  footerGroup: GroupData | null;
  cartDrawerGroup: GroupData | null;
}

/** A full draft persisted to localStorage between sessions. */
export interface DraftSnapshot {
  theme: {
    globalSettings: Record<string, any>;
    headerGroup: GroupData | null;
    footerGroup: GroupData | null;
    cartDrawerGroup: GroupData | null;
  };
  pages: Record<string, { sections: Record<string, SectionMetadata>; order: string[] }>;
  timestamp: number;
}

type HistoryEntry = {
  label: string;
  sections: Record<string, SectionMetadata>;
  order: string[];
  headerGroup: GroupData | null;
  footerGroup: GroupData | null;
  cartDrawerGroup: GroupData | null;
  globalSettings: Record<string, any>;
};

interface EditorState {
  // --- session
  session: EditorSession | null;

  // --- metadata for the CURRENT page (schemas, files, data)
  metadata: EditorMetadata | null;
  initialHtml: string;
  page: string;

  // --- CURRENT page editable state (lives at top-level for convenience)
  sections: Record<string, SectionMetadata>;
  order: string[];

  // --- THEME-WIDE editable state (stays across page switches)
  globalSettings: Record<string, any>;
  headerGroup: GroupData | null;
  footerGroup: GroupData | null;
  cartDrawerGroup: GroupData | null;

  // --- Baselines (server truth)
  themeBaseline: ThemeBaseline | null;
  pageBaselines: Record<string, PageBaseline>;

  // --- Cache of non-current pages (for "edit multiple pages then save")
  pageCache: Record<string, PageSlice>;

  // --- pending draft awaiting user decision on boot
  pendingDraft: DraftSnapshot | null;

  // --- UI
  selection: Selection;
  device: Device;
  mode: Mode;
  inspectorOpen: boolean;
  /**
   * When true, the preview iframe shows hover outlines + click-to-select
   * chrome so the user can pick sections visually. When false, the iframe
   * behaves like the real storefront — links click, forms submit — useful
   * for testing theme JS or just inspecting the page without the editor's
   * overlay in the way.
   */
  previewEditable: boolean;
  loading: boolean;
  error: string | null;
  saveStatus: SaveStatus;

  // --- preview context overrides (only matter on pages like product/collection)
  previewProductSlug: string | null;
  previewCollectionId: string | null;

  /**
   * Quando o usuário seleciona "Carrinho (gaveta/mensagem)" no
   * dropdown de páginas, a gente não navega pra `cart.json` — em vez
   * disso dispara `theme:cart:open` no iframe pra emular o trigger
   * do carrinho sobre a página atual. Mas se depois disso o
   * iframe recarregar (config global mudou → scheduleLayoutReload),
   * o drawer fecha junto com o reload.
   *
   * Essa flag persiste o estado "usuário tá em modo cart drawer/
   * mensagem" através dos reloads — o Preview.tsx dispara o evento
   * de novo no onLoad do iframe quando esse valor não é null.
   */
  cartPreviewMode: 'drawer' | 'message' | null;

  // --- theme sync (upgrade report from the latest publish)
  themeSync: ThemeSyncReport | null;
  /*
    Erro do auto-sync-on-boot, se houve. Antes o `.catch` só imprimia
    console.warn e o usuário nunca sabia que o sync falhou — resultado:
    editor mostrava content fresh via preview, mas a loja em prod ficava
    servindo o path antigo do tenant indefinidamente. Reportado por
    cremecacau. Agora o erro sobe pro state e o `ThemeSyncBanner` mostra
    uma variante "erro" com o motivo pra que o lojista peça help.
  */
  themeSyncError: string | null;

  // --- history (per-page, transient)
  past: HistoryEntry[];
  future: HistoryEntry[];

  // --- actions
  boot(session: EditorSession, page: string): Promise<void>;
  reloadPage(): Promise<void>;
  /**
   * Force-refresh do estado a partir do servidor SEM mandar os drafts em
   * memória. Diferente de `reloadPage()`, que usa o estado local como
   * "draft override" pra preservar edições não-salvas, esse método
   * descarta o estado local e adota o que o servidor diz.
   *
   * Use quando uma fonte EXTERNA (Luna apply, sync from default, importer)
   * tiver escrito direto no storage e o frontend precise se sincronizar.
   * Sem isso, `reloadPage` enviaria o estado in-memory antigo como draft,
   * sobrescrevendo invisívelmente o que acabou de ser persistido.
   */
  reloadPageFromServer(): Promise<void>;
  setPage(page: string): Promise<void>;
  select(selection: Selection): void;
  setDevice(device: Device): void;
  setMode(mode: Mode): void;
  setInspectorOpen(open: boolean): void;
  setPreviewEditable(editable: boolean): void;
  setPreviewProduct(slug: string | null): Promise<void>;
  setPreviewCollection(id: string | null): Promise<void>;
  setCartPreviewMode(mode: 'drawer' | 'message' | null): void;

  updateSectionSetting(
    ref: { group: SectionGroup; sectionId: string },
    settingId: string,
    value: any
  ): void;
  updateBlockSetting(
    ref: { group: SectionGroup; sectionId: string; blockId: string },
    settingId: string,
    value: any
  ): void;
  updateGlobalSetting(settingId: string, value: any): void;
  reorderSections(group: SectionGroup, newOrder: string[]): void;
  addSection(group: SectionGroup, type: string, index?: number): Promise<string>;
  removeSection(ref: { group: SectionGroup; sectionId: string }): Promise<void>;
  toggleSectionDisabled(ref: { group: SectionGroup; sectionId: string }): void;

  addBlock(
    ref: { group: SectionGroup; sectionId: string },
    blockType: string,
    index?: number
  ): string | null;
  removeBlock(ref: { group: SectionGroup; sectionId: string; blockId: string }): void;
  reorderBlocks(ref: { group: SectionGroup; sectionId: string }, newOrder: string[]): void;
  toggleBlockDisabled(ref: { group: SectionGroup; sectionId: string; blockId: string }): void;

  save(): Promise<void>;
  discardChanges(): void;
  restoreDraft(): void;
  dismissDraft(): void;
  setSaveStatus(status: SaveStatus): void;
  dismissThemeSync(): void;
  dismissThemeSyncError(): void;

  undo(): void;
  redo(): void;

  _snapshot(label: string): void;
  _restore(entry: HistoryEntry): void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getGroupData(
  s: Pick<EditorState, 'sections' | 'order' | 'headerGroup' | 'footerGroup' | 'cartDrawerGroup'>,
  group: SectionGroup
): GroupData {
  if (group === 'template') return { sections: s.sections, order: s.order };
  if (group === 'header-group') return s.headerGroup || { sections: {}, order: [] };
  if (group === 'cart-drawer-group') return s.cartDrawerGroup || { sections: {}, order: [] };
  return s.footerGroup || { sections: {}, order: [] };
}

function cloneSection(s: SectionMetadata): SectionMetadata {
  return JSON.parse(JSON.stringify(s));
}

function writeSectionIntoGroup<S extends Pick<EditorState, 'sections' | 'headerGroup' | 'footerGroup' | 'cartDrawerGroup' | 'selection'>>(
  state: S,
  group: SectionGroup,
  sectionId: string,
  updated: SectionMetadata
): S {
  if (group === 'template') {
    return { ...state, sections: { ...state.sections, [sectionId]: updated } };
  }
  if (group === 'header-group' && state.headerGroup) {
    return {
      ...state,
      headerGroup: {
        ...state.headerGroup,
        sections: { ...state.headerGroup.sections, [sectionId]: updated },
      },
    };
  }
  if (group === 'footer-group' && state.footerGroup) {
    return {
      ...state,
      footerGroup: {
        ...state.footerGroup,
        sections: { ...state.footerGroup.sections, [sectionId]: updated },
      },
    };
  }
  if (group === 'cart-drawer-group' && state.cartDrawerGroup) {
    return {
      ...state,
      cartDrawerGroup: {
        ...state.cartDrawerGroup,
        sections: { ...state.cartDrawerGroup.sections, [sectionId]: updated },
      },
    };
  }
  return state;
}

function generateSectionId(type: string): string {
  const suffix = Math.random().toString(36).slice(2, 8);
  return `${type.replace(/[^a-zA-Z0-9]/g, '_')}_${suffix}`;
}

function buildDefaultSection(type: string, schema: SectionSchema | undefined): SectionMetadata {
  const settings: Record<string, any> = {};
  if (schema?.presets?.[0]?.settings) Object.assign(settings, schema.presets[0].settings);
  for (const s of schema?.settings || []) {
    if (s.id && !(s.id in settings) && s.default !== undefined) settings[s.id] = s.default;
  }
  const blocks: Record<string, any> = {};
  const blockOrder: string[] = [];
  const presetBlocks = schema?.presets?.[0]?.blocks || [];
  for (const b of presetBlocks) {
    const id = `b_${Math.random().toString(36).slice(2, 8)}`;
    const bSchema = (schema?.blocks || []).find((x) => x.type === b.type);
    const bSettings: Record<string, any> = { ...(b.settings || {}) };
    for (const s of bSchema?.settings || []) {
      if (s.id && !(s.id in bSettings) && s.default !== undefined) bSettings[s.id] = s.default;
    }
    blocks[id] = { type: b.type, settings: bSettings };
    blockOrder.push(id);
  }
  return { type, settings, blocks, blockOrder, disabled: false };
}

// ---------------------------------------------------------------------------
// Dirty detection
// ---------------------------------------------------------------------------

/** True if the current editor state differs from any baseline on record. */
export function isDirty(s: EditorState): boolean {
  return dirtyCount(s) > 0;
}

/**
 * Counts how many distinct edits are pending across every visited page
 * and every theme-wide bucket. Used by the Toolbar to show "Salvar N".
 */
export function dirtyCount(s: EditorState): number {
  if (!s.themeBaseline) return 0;
  let n = 0;

  // Theme-wide
  if (JSON.stringify(s.globalSettings) !== JSON.stringify(s.themeBaseline.globalSettings)) n++;
  if (JSON.stringify(s.headerGroup) !== JSON.stringify(s.themeBaseline.headerGroup)) n++;
  if (JSON.stringify(s.footerGroup) !== JSON.stringify(s.themeBaseline.footerGroup)) n++;
  /*
    cartDrawerGroup faltava aqui — mudança em settings de cart-drawer-config
    (border-radius, cores, combo, etc.) não ativava o botão Salvar.
    User reportou: "altero a borda pra outro valor e o botao de salvar nao
    ativa, como se nao tivesse o que salvar". Bug clássico de baseline-diff
    incompleto: cartDrawerGroup foi adicionado depois mas o dirty-counter
    não foi atualizado pra incluir ele.
  */
  if (JSON.stringify(s.cartDrawerGroup) !== JSON.stringify(s.themeBaseline.cartDrawerGroup)) n++;

  // Current page
  n += countPageDirty(
    { sections: s.sections, order: s.order },
    s.pageBaselines[s.page]
  );

  // Cached pages (those visited but not current)
  for (const [page, slice] of Object.entries(s.pageCache)) {
    if (page === s.page) continue;
    n += countPageDirty(
      { sections: slice.sections, order: slice.order },
      s.pageBaselines[page]
    );
  }

  return n;
}

function countPageDirty(
  cur: { sections: Record<string, SectionMetadata>; order: string[] },
  base: PageBaseline | undefined
): number {
  if (!base) return 0;
  let n = 0;
  if (JSON.stringify(cur.order) !== JSON.stringify(base.order)) n++;
  const ids = new Set([...Object.keys(cur.sections), ...Object.keys(base.sections)]);
  for (const id of ids) {
    if (JSON.stringify(cur.sections[id]) !== JSON.stringify(base.sections[id])) n++;
  }
  return n;
}

/**
 * List of pages that have any pending change. Includes the current page
 * when dirty. Drives the Toolbar's "Salvar" tooltip.
 */
export function dirtyPages(s: EditorState): string[] {
  const out: string[] = [];
  if (!s.themeBaseline) return out;
  if (
    countPageDirty({ sections: s.sections, order: s.order }, s.pageBaselines[s.page]) > 0
  ) {
    out.push(s.page);
  }
  for (const [page, slice] of Object.entries(s.pageCache)) {
    if (page === s.page) continue;
    if (countPageDirty(slice, s.pageBaselines[page]) > 0) out.push(page);
  }
  return out;
}

/**
 * Re-runs the section CSS var discoverer over the liquid files in the
 * response and pushes the bindings to the patcher. Called from boot
 * AND from every reload path so live-patch picks up new vars from
 * AI-created sections / snippets without requiring an F5.
 *
 * Bug que motivou: user reportou "se a IA cria uma config nova, em
 * uma section, e eu altero essa config, só dando F5 para ver a
 * alteracao". Causa: discoverer rodava só no boot — quando a AI
 * adicionava arquivos novos via Luna apply, o reload subsequente
 * atualizava state + iframe mas o mapa de bindings ficava com a
 * versão do boot. Live-patch caía em rerender pra config nova, e
 * em alguns casos o rerender em si não pegava por outro motivo
 * (timing, cache, etc.) — só F5 forçava re-discovery.
 *
 * Solução: chamar essa função em todo lugar que recebe uma response
 * fresca do `/render-for-editor`.
 */
function rebuildSectionCssVarMap(res: EditorResponse): void {
  /*
    Mapa scopado por SECTION TYPE: { 'contact-form': { background: [bindings] } }.
    Sem o scope, settings com mesmo id em sections diferentes
    (ex.: `background` aparece em contact-form, search-results,
    featured-collections, etc.) colidiam — discoverer agrupava tudo
    num único `settingId → bindings[]` e o patcher tocava vars de
    sections que o user nem estava editando. Pior: a falsa
    "patchedAnyVar = true" suprimia o fallback de re-render, deixando
    a section visível com o valor antigo.
  */
  const map: Record<string, Record<string, SectionCssVarBinding[]>> = {};
  const themeFiles = res.metadata?.theme?.files || {};
  let totalBindings = 0;
  for (const [path, content] of Object.entries(themeFiles)) {
    if (typeof content !== 'string') continue;
    if (!path.endsWith('.liquid')) continue;
    /*
      Só sections — snippets podem ser usados por várias sections e
      misturariam scope. Section settings ficam SEMPRE no .liquid da
      própria section (ou nele importando snippet via render — nesse
      caso o discoverer não pega o snippet, mas é OK porque section
      settings tipicamente vivem no <style> da section em si).
    */
    if (!path.startsWith('sections/')) continue;
    const match = path.match(/^sections\/([^/]+)\.liquid$/);
    if (!match) continue;
    const sectionType = match[1];
    const partial = discoverSectionCssVarsFromLiquid(content);
    if (!map[sectionType]) map[sectionType] = {};
    for (const [settingId, bindings] of Object.entries(partial)) {
      if (!map[sectionType][settingId]) map[sectionType][settingId] = [];
      for (const b of bindings) {
        const existing = map[sectionType][settingId].find(
          (x) => x.var === b.var && x.unit === b.unit
        );
        if (existing) {
          if (b.rootScoped && !existing.rootScoped) existing.rootScoped = true;
        } else {
          map[sectionType][settingId].push(b);
          totalBindings++;
        }
      }
    }
  }
  setDiscoveredSectionCssVars(map);
  console.info(
    `[editor] rebuilt section CSS var map: ${Object.keys(map).length} section types, ${totalBindings} bindings`
  );
}

// ---------------------------------------------------------------------------
// Draft persistence (localStorage)
// ---------------------------------------------------------------------------

function draftKey(session: EditorSession): string {
  return `lc-editor-draft:${session.tenantId}:${session.themeId}`;
}

function takeDraft(s: EditorState): DraftSnapshot {
  const pages: DraftSnapshot['pages'] = {};
  // current page
  pages[s.page] = {
    sections: JSON.parse(JSON.stringify(s.sections)),
    order: [...s.order],
  };
  // cached pages
  for (const [page, slice] of Object.entries(s.pageCache)) {
    if (page === s.page) continue;
    pages[page] = {
      sections: JSON.parse(JSON.stringify(slice.sections)),
      order: [...slice.order],
    };
  }
  return {
    theme: {
      globalSettings: JSON.parse(JSON.stringify(s.globalSettings)),
      headerGroup: s.headerGroup ? JSON.parse(JSON.stringify(s.headerGroup)) : null,
      footerGroup: s.footerGroup ? JSON.parse(JSON.stringify(s.footerGroup)) : null,
      cartDrawerGroup: s.cartDrawerGroup ? JSON.parse(JSON.stringify(s.cartDrawerGroup)) : null,
    },
    pages,
    timestamp: Date.now(),
  };
}

function draftDiffersFromBaselines(d: DraftSnapshot, s: EditorState): boolean {
  if (!s.themeBaseline) return true;
  if (JSON.stringify(d.theme.globalSettings) !== JSON.stringify(s.themeBaseline.globalSettings))
    return true;
  if (JSON.stringify(d.theme.headerGroup) !== JSON.stringify(s.themeBaseline.headerGroup))
    return true;
  if (JSON.stringify(d.theme.footerGroup) !== JSON.stringify(s.themeBaseline.footerGroup))
    return true;
  /*
    BUG FIX: cartDrawerGroup era ignorado aqui, então quando o user
    mexia SOMENTE em settings do cart-drawer-config (combo_enabled,
    parcelas, etc), `draftDiffersFromBaselines` retornava false →
    `persistDraft` removia o localStorage entry → reload perdia
    todas as mudanças não-salvas do drawer.

    User reportou: "no editor ta marcado [combo] mas no liquid nao
    ta mostrando" — exatamente esse caso (state UI mostra true, mas
    draft não persiste, então no boot iframe renderiza com persisted
    false e state também volta pra false).
  */
  if (JSON.stringify(d.theme.cartDrawerGroup) !== JSON.stringify(s.themeBaseline.cartDrawerGroup))
    return true;
  for (const [page, p] of Object.entries(d.pages)) {
    const base = s.pageBaselines[page];
    if (!base) return true; // draft has a page we haven't loaded → can't prove equal
    if (countPageDirty(p, base) > 0) return true;
  }
  return false;
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const useEditor = create<EditorState>((set, get) => {
  const persistDraft = debounce(() => {
    const s = get();
    if (!s.session || !s.themeBaseline) return;
    const snap = takeDraft(s);
    try {
      if (draftDiffersFromBaselines(snap, s)) {
        localStorage.setItem(draftKey(s.session), JSON.stringify(snap));
      } else {
        localStorage.removeItem(draftKey(s.session));
      }
    } catch {}
  }, 300);

  function mutate(label: string, apply: () => void) {
    get()._snapshot(label);
    apply();
    persistDraft();
  }

  return {
    session: null,
    metadata: null,
    initialHtml: '',
    page: 'index',

    sections: {},
    order: [],
    headerGroup: null,
    footerGroup: null,
    cartDrawerGroup: null,
    globalSettings: {},

    themeBaseline: null,
    pageBaselines: {},
    pageCache: {},
    pendingDraft: null,

    selection: null,
    device: 'desktop',
    mode: 'visual',
    inspectorOpen: (() => {
      try {
        const raw = localStorage.getItem('lc-editor-inspector-open');
        return raw == null ? true : raw === '1';
      } catch {
        return true;
      }
    })(),
    previewEditable: (() => {
      try {
        const raw = localStorage.getItem('lc-editor-preview-editable');
        return raw == null ? true : raw === '1';
      } catch {
        return true;
      }
    })(),
    loading: false,
    error: null,
    saveStatus: { state: 'idle' },

    previewProductSlug: null,
    previewCollectionId: null,
    cartPreviewMode: null,
    themeSync: null,
    themeSyncError: null,

    past: [],
    future: [],

    async boot(session, page) {
      set({
        session,
        page,
        loading: true,
        error: null,
        pendingDraft: null,
        pageCache: {},
        pageBaselines: {},
        themeSync: null,
        themeSyncError: null,
      });

      // Fire the sync in the background — if the default theme advanced
      // since the tenant's last boot, this pulls new files and merges
      // new setting defaults. Done in parallel with the page fetch so
      // first paint isn't blocked. We consume the result on the next
      // reload, and surface an "upgraded" toast if anything actually
      // changed.
      syncThemeFromDefault(session).then(
        (report) => {
          if (report.upgraded) {
            set({ themeSync: report });
            // Drop file cache so the freshly-synced sections/snippets
            // are loaded instead of the stale ones we just fetched.
            void get().reloadPage();
          }
        },
        (err) => {
          /*
            Antes: só `console.warn(...)` — usuário nunca sabia que o sync
            falhou, e a loja em prod ficava servindo o path antigo por
            tempo indeterminado. Agora sobe pro state pra o
            `ThemeSyncBanner` renderizar variante "erro" com o motivo.
            Continua non-fatal: o editor ainda boota normalmente com os
            arquivos que já estão no path do tenant.
          */
          const msg = err?.message || String(err);
          console.warn('[editor] theme sync failed (non-fatal)', err);
          set({ themeSyncError: msg });
        }
      );

      // Lê snippets/css-variables.liquid do tema e monta o mapa de
      // `{ settingId: [cssVarName, ...] }` automaticamente. Assim toda
      // vez que uma global setting muda, a gente sabe exatamente quais
      // custom properties tocar em `:root` — SEM precisar manter um
      // mapa hardcoded no editor. Qualquer alias novo que o tema
      // introduzir vira patch live na próxima boot.
      //
      // Fire-and-forget: se falhar (arquivo não existe, 404, timeout),
      // o fallback hardcoded em css-var-patcher.ts ainda cobre o essencial.
      readThemeFile(session, 'snippets/css-variables.liquid').then(
        (content) => {
          const map = discoverCssVarsFromLiquid(content);
          setDiscoveredCssVars(map);
          const n = Object.keys(map).length;
          console.info(`[editor] discovered ${n} setting → CSS var mapping(s) from css-variables.liquid`);
        },
        (err) => {
          console.warn('[editor] could not read css-variables.liquid for live patching', err?.message || err);
        }
      );

      try {
        const { previewProductSlug, previewCollectionId } = get();

        /*
          UX FIX: F5 deve ser um "refresh limpo".

          Anteriormente o boot lia o draft do localStorage e mandava
          como `drafts.files`/`drafts.globalSettings` pro engine, de
          modo que inspector E iframe começavam já com os valores não-
          salvos aplicados silenciosamente. O banner `UnsavedDraftBanner`
          nunca aparecia (porque `draftDiffersFromBaselines` comparava
          o draft contra um baseline tirado DEPOIS do hydrate, e o
          hydrate já tinha o draft mergeado → baseline = draft → diff =
          false → pendingDraft nunca era setado).

          User reportou: "alinhamento estava no centro, dou f5, ele volta
          pro centro, ok eu nao salvei tá certo. Porém nas configs do
          editor mostra selecionado a esquerda" — sintoma do auto-restore
          silencioso, com o iframe re-renderizando server values em
          algum caminho mas o inspector ficando com o draft.

          Comportamento novo:
            1. Boot NUNCA manda drafts — sempre fetch server-clean.
            2. State é hydratado com os valores do servidor → inspector
               e iframe mostram o MESMO valor (saved).
            3. Se localStorage tem draft, mostramos o banner pro user
               decidir Restaurar (re-render iframe com drafts) ou
               Descartar (limpar localStorage).
            4. Restaurar chama reloadPage() no fim pra garantir que o
               iframe pega os drafts via re-render (cobre settings que
               não tem live-patch).
        */
        const res = await fetchEditorPage(session, page, {
          nocache: true,
          productSlug: previewProductSlug || undefined,
          collectionId: previewCollectionId || undefined,
        });
        hydrateFromResponse(set, res, page);

        rebuildSectionCssVarMap(res);
        // Capture baselines (theme + this page)
        const s = get();
        const themeBaseline: ThemeBaseline = {
          globalSettings: JSON.parse(JSON.stringify(s.globalSettings)),
          headerGroup: s.headerGroup ? JSON.parse(JSON.stringify(s.headerGroup)) : null,
          footerGroup: s.footerGroup ? JSON.parse(JSON.stringify(s.footerGroup)) : null,
          cartDrawerGroup: s.cartDrawerGroup ? JSON.parse(JSON.stringify(s.cartDrawerGroup)) : null,
        };
        const pageBaseline: PageBaseline = {
          sections: JSON.parse(JSON.stringify(s.sections)),
          order: [...s.order],
        };
        set({
          themeBaseline,
          pageBaselines: { [page]: pageBaseline },
        });

        // Inspect localStorage for a draft
        const draft = loadDraftFromStorage(session);
        if (draft && draftDiffersFromBaselines(draft, get())) {
          set({ pendingDraft: draft });
        } else if (draft) {
          try { localStorage.removeItem(draftKey(session)); } catch {}
        }
      } catch (err: any) {
        set({ error: err.message, loading: false });
      }
    },

    async reloadPage() {
      const state = get();
      const {
        session,
        page,
        previewProductSlug,
        previewCollectionId,
        globalSettings,
        sections,
        order,
        headerGroup,
        footerGroup,
        cartDrawerGroup,
      } = state;
      if (!session) return;
      set({ loading: true });

      // Always send the store's globalSettings as drafts so the
      // engine renders with whatever the user has in-flight. Earlier
      // we gated this on "differs from baseline" which meant that a
      // drag that happened to not change JSON.stringify (e.g. same
      // shape, different internal order) would skip the draft path
      // entirely and the engine would return HTML rendered with the
      // OLD persisted settings. Sending unconditionally is a few
      // extra bytes in the POST body — the engine is cheap about
      // applying the override.
      //
      // ALSO send the live section configuration for header-group,
      // footer-group and the current page template as draft files.
      // Without this, the engine reads the PERSISTED JSON from S3
      // and ignores every section-level edit the user hasn't saved
      // yet — menu picks, block reorders, per-section color tweaks
      // all vanish from the preview on the next layout reload.
      // drafts.files uses the file-system hook inside the engine's
      // renderForEditor so the runtime transparently serves our
      // in-memory JSON instead of the S3 object.
      const files: Record<string, string> = {};
      if (headerGroup) {
        files['sections/header-group.json'] = JSON.stringify(
          serialiseGroupForEngine(headerGroup)
        );
      }
      if (footerGroup) {
        files['sections/footer-group.json'] = JSON.stringify(
          serialiseGroupForEngine(footerGroup)
        );
      }
      if (cartDrawerGroup) {
        files['sections/cart-drawer-group.json'] = JSON.stringify(
          serialiseGroupForEngine(cartDrawerGroup)
        );
      }
      files[`templates/${page}.json`] = JSON.stringify(
        serialiseGroupForEngine({ sections, order })
      );

      const drafts = { globalSettings, files };

      try {
        const res = await fetchEditorPage(session, page, {
          nocache: true,
          productSlug: previewProductSlug || undefined,
          collectionId: previewCollectionId || undefined,
          drafts,
        });
        // Preserve the store's in-progress drafts — otherwise the
        // hydrate would write the server's persisted globals back
        // over the user's changes and the inspector would appear to
        // "revert".
        hydrateFromResponse(set, res, page, { preserveDrafts: true });
        // Re-discover CSS var bindings — picks up new sections/snippets
        // the AI may have added since boot, so live-patch works without
        // requiring F5.
        rebuildSectionCssVarMap(res);
        const s = get();
        set({
          pageBaselines: {
            ...s.pageBaselines,
            [page]: {
              sections: JSON.parse(JSON.stringify(s.sections)),
              order: [...s.order],
            },
          },
        });
      } catch (err: any) {
        set({ error: err.message, loading: false });
      }
    },

    async reloadPageFromServer() {
      const { session, page, previewProductSlug, previewCollectionId } = get();
      if (!session) return;
      set({ loading: true });
      /*
        Limpa pageCache pra essa página + páginas adjacentes — o cache
        guarda `initialHtml` antigo. Sem isso, navegar pra outra página
        e voltar mostraria a versão pre-apply.

        Limpamos TODAS as páginas (não só a atual) pra cobrir casos onde
        o save afeta múltiplas páginas (ex.: edição de header-group
        impacta toda página que renderiza o header).
      */
      set({ pageCache: {} });
      try {
        /*
          Sem `drafts` — engine lê o estado persistido (S3/Mongo/local)
          como fonte da verdade. Usado depois de Luna apply / sync /
          import quando o storage mudou por fora.

          `nocache: true` força o engine a SKIP do Redis cache de
          editor renders. Combinado com o `clearFileCache()` que o
          `writeThemeFile` chamou, garante leitura S3 fresca.
        */
        const res = await fetchEditorPage(session, page, {
          nocache: true,
          productSlug: previewProductSlug || undefined,
          collectionId: previewCollectionId || undefined,
        });
        // Hydrate FULL (sem preserveDrafts) — descarta estado local em
        // favor do servidor. Isso atualiza headerGroup/footerGroup/
        // cartDrawerGroup/globalSettings + sections/order do template,
        // e troca o `initialHtml` que o Preview usa pra setar `srcdoc`.
        // O iframe re-renderiza no useEffect que observa initialHtml.
        hydrateFromResponse(set, res, page);
        // Re-discover CSS var bindings — sem isso, AI cria section nova
        // com `--var: {{ section.settings.X }}` mas o mapa fica com a
        // versão do boot, e live-patch da setting nova cai em rerender.
        rebuildSectionCssVarMap(res);
        const s = get();
        /*
          Reset COMPLETO dos baselines (theme + page) pro estado servidor.

          Antes só atualizava `pageBaselines[page]` — `themeBaseline`
          ficava STALE da boot. Isso causava bug crítico: depois de
          Luna apply (AI cria section), o save subsequente diffaria
          `state.headerGroup` (com section da AI) vs
          `themeBaseline.headerGroup` (sem). Save enviava
          `apiAddSection` pra section que JÁ EXISTIA no S3 →
          `addSectionToGroup` no engine fazia `data.order.push(id)`
          unconditionally → duplicava o ID em `order`. Pior:
          `saveSectionOrder` sobrescrevia o order do S3 com state.order
          que podia não ter o ID da AI (timing/race) → section órfã.

          User reportou: "tudo que crio com a IA quando salva, morre".
          Causa raiz: themeBaseline stale fazendo save tratar mudanças
          já persistidas pela AI como "novas adições".

          Fix: depois de hydrateFromResponse, RESETAR themeBaseline
          também. Save subsequente vê estado igual ao baseline → no-op
          pros itens que a AI já persistiu, e só diffs reais (mudanças
          do usuário depois do apply) são enviados.
        */
        set({
          pageBaselines: {
            ...s.pageBaselines,
            [page]: {
              sections: JSON.parse(JSON.stringify(s.sections)),
              order: [...s.order],
            },
          },
          themeBaseline: {
            globalSettings: JSON.parse(JSON.stringify(s.globalSettings)),
            headerGroup: s.headerGroup ? JSON.parse(JSON.stringify(s.headerGroup)) : null,
            footerGroup: s.footerGroup ? JSON.parse(JSON.stringify(s.footerGroup)) : null,
            cartDrawerGroup: s.cartDrawerGroup ? JSON.parse(JSON.stringify(s.cartDrawerGroup)) : null,
          },
        });
      } catch (err: any) {
        set({ error: err.message, loading: false });
      }
    },

    async setPage(newPage) {
      const { session, page: oldPage, previewProductSlug, previewCollectionId } = get();
      if (!session || newPage === oldPage) return;

      // 1) Stash the current page's live state into pageCache so we don't
      //    lose any in-memory edits when the user navigates back later.
      set((s) => ({
        pageCache: {
          ...s.pageCache,
          [oldPage]: {
            sections: JSON.parse(JSON.stringify(s.sections)),
            order: [...s.order],
            initialHtml: s.initialHtml,
            metadata: s.metadata!,
          },
        },
      }));

      // 2) Load the new page — from cache if visited before, else server.
      const cached = get().pageCache[newPage];
      if (cached) {
        set({
          page: newPage,
          sections: cached.sections,
          order: cached.order,
          initialHtml: cached.initialHtml,
          metadata: cached.metadata,
          loading: false,
          selection: null,
          past: [],
          future: [],
        });

        // cached.initialHtml é o HTML do server capturado no OUT — não
        // reflete edits in-memory feitos via /render-section depois disso.
        // Se a página cacheada está dirty, o iframe seria montado com HTML
        // pré-edit; reloadPage envia os drafts e traz HTML atualizado.
        const baseAfter = get().pageBaselines[newPage];
        const isPageDirty = baseAfter
          ? countPageDirty(
              { sections: cached.sections, order: cached.order },
              baseAfter,
            ) > 0
          : false;
        if (isPageDirty) {
          void get().reloadPage();
        }
        return;
      }

      set({ page: newPage, loading: true });
      try {
        /*
          BUG FIX: setPage não enviava drafts.

          User reportou: "mesmo alterando a config para página, no
          select de páginas aparece carrinho ok, mas quando vou para
          ele, ta abrindo a gaveta. Isso estava certo antes."

          Causa: o seller mudou cart_type='page' no inspector
          (estado em memória), depois clicou em "Carrinho" pra
          navegar. setPage('cart') fazia fetch SEM `drafts`, então o
          engine renderizava com o cart_type PERSISTIDO (ainda
          'drawer'). A Liquid emitia `var cartType='drawer';`, o
          auto-open script ativava o drawer.

          Fix: envia drafts.globalSettings + drafts.files (mesmo
          padrão do reloadPage e boot) pra o engine respeitar as
          mudanças unsaved do inspector durante a navegação.
        */
        const s0 = get();
        const draftFiles: Record<string, string> = {};
        if (s0.headerGroup) {
          draftFiles['sections/header-group.json'] = JSON.stringify(
            serialiseGroupForEngine(s0.headerGroup)
          );
        }
        if (s0.footerGroup) {
          draftFiles['sections/footer-group.json'] = JSON.stringify(
            serialiseGroupForEngine(s0.footerGroup)
          );
        }
        if (s0.cartDrawerGroup) {
          draftFiles['sections/cart-drawer-group.json'] = JSON.stringify(
            serialiseGroupForEngine(s0.cartDrawerGroup)
          );
        }
        // Template da página de DESTINO (pode ter draft de sections do
        // pageCache se user já mexeu nela antes).
        const cachedDest = s0.pageCache[newPage];
        if (cachedDest) {
          draftFiles[`templates/${newPage}.json`] = JSON.stringify(
            serialiseGroupForEngine({
              sections: cachedDest.sections,
              order: cachedDest.order,
            })
          );
        }

        const res = await fetchEditorPage(session, newPage, {
          nocache: true,
          productSlug: previewProductSlug || undefined,
          collectionId: previewCollectionId || undefined,
          drafts: {
            globalSettings: s0.globalSettings,
            files: draftFiles,
          },
        });
        // Hydrate sections/order (page-scoped). Theme-wide state is
        // intentionally NOT replaced by the server response — it must
        // carry across pages until Salvar resets baselines.
        hydratePageFromResponse(set, res, newPage);
        const s = get();
        if (!s.pageBaselines[newPage]) {
          set({
            pageBaselines: {
              ...s.pageBaselines,
              [newPage]: {
                sections: JSON.parse(JSON.stringify(s.sections)),
                order: [...s.order],
              },
            },
          });
        }
      } catch (err: any) {
        console.error('[editor] setPage failed', newPage, err);
        set({
          loading: false,
          saveStatus: {
            state: 'error',
            message: `Não foi possível carregar "${newPage}": ${err.message || 'erro desconhecido'}`,
          },
        });
      }
    },

    select(selection) { set({ selection }); },
    setDevice(device) { set({ device }); },

    setMode(mode) {
      set({ mode });
      try {
        const url = new URL(window.location.href);
        if (mode === 'code') url.searchParams.set('mode', 'code');
        else url.searchParams.delete('mode');
        window.history.replaceState({}, '', url.toString());
      } catch {}
    },

    setInspectorOpen(open) {
      set({ inspectorOpen: open });
      try {
        localStorage.setItem('lc-editor-inspector-open', open ? '1' : '0');
      } catch {}
    },

    setPreviewEditable(editable) {
      set({ previewEditable: editable });
      // When the overlay is turned off we also clear any selection so the
      // user doesn't see a stale "selected" indicator hanging on a
      // section they can no longer interact with.
      if (!editable) set({ selection: null });
      try {
        localStorage.setItem('lc-editor-preview-editable', editable ? '1' : '0');
      } catch {}
    },

    async setPreviewProduct(slug) {
      set({ previewProductSlug: slug });
      if (get().page === 'product') await get().reloadPage();
    },

    setCartPreviewMode(mode) {
      set({ cartPreviewMode: mode });
    },

    async setPreviewCollection(id) {
      set({ previewCollectionId: id });
      if (get().page === 'collection') await get().reloadPage();
    },

    // ---------------------------------------------------------------- edits
    updateSectionSetting({ group, sectionId }, settingId, value) {
      mutate(`edit ${sectionId}.${settingId}`, () => {
        set((s) => {
          const current = getGroupData(s, group).sections[sectionId];
          if (!current) return s;
          const updated = cloneSection(current);
          updated.settings = { ...updated.settings, [settingId]: value };
          return writeSectionIntoGroup(s, group, sectionId, updated);
        });
      });
    },

    updateBlockSetting({ group, sectionId, blockId }, settingId, value) {
      mutate(`edit block ${blockId}.${settingId}`, () => {
        set((s) => {
          const current = getGroupData(s, group).sections[sectionId];
          if (!current) return s;
          const updated = cloneSection(current);
          const block = updated.blocks[blockId] || { type: '', settings: {} };
          block.settings = { ...(block.settings || {}), [settingId]: value };
          updated.blocks = { ...updated.blocks, [blockId]: block };
          return writeSectionIntoGroup(s, group, sectionId, updated);
        });
      });
    },

    updateGlobalSetting(settingId, value) {
      mutate(`global ${settingId}`, () => {
        set((s) => ({ globalSettings: { ...s.globalSettings, [settingId]: value } }));
      });
    },

    reorderSections(group, newOrder) {
      mutate(`reorder ${group}`, () => {
        set((s) => {
          if (group === 'template') return { ...s, order: [...newOrder] };
          if (group === 'header-group' && s.headerGroup) {
            return { ...s, headerGroup: { ...s.headerGroup, order: [...newOrder] } };
          }
          if (group === 'footer-group' && s.footerGroup) {
            return { ...s, footerGroup: { ...s.footerGroup, order: [...newOrder] } };
          }
          if (group === 'cart-drawer-group' && s.cartDrawerGroup) {
            return { ...s, cartDrawerGroup: { ...s.cartDrawerGroup, order: [...newOrder] } };
          }
          return s;
        });
      });
    },

    async addSection(group, type, index) {
      const { metadata } = get();
      if (!metadata) throw new Error('editor not ready');
      const schema = metadata.theme.schemas[type];
      const sectionId = generateSectionId(type);
      const section = buildDefaultSection(type, schema);

      mutate(`add ${type}`, () => {
        set((s) => {
          if (group === 'template') {
            const order = [...s.order];
            if (typeof index === 'number') order.splice(index, 0, sectionId);
            else order.push(sectionId);
            return {
              ...s,
              sections: { ...s.sections, [sectionId]: section },
              order,
            };
          }
          if (group === 'header-group' && s.headerGroup) {
            const order = [...s.headerGroup.order];
            if (typeof index === 'number') order.splice(index, 0, sectionId);
            else order.push(sectionId);
            return {
              ...s,
              headerGroup: {
                sections: { ...s.headerGroup.sections, [sectionId]: section },
                order,
              },
            };
          }
          if (group === 'footer-group' && s.footerGroup) {
            const order = [...s.footerGroup.order];
            if (typeof index === 'number') order.splice(index, 0, sectionId);
            else order.push(sectionId);
            return {
              ...s,
              footerGroup: {
                sections: { ...s.footerGroup.sections, [sectionId]: section },
                order,
              },
            };
          }
          if (group === 'cart-drawer-group' && s.cartDrawerGroup) {
            const order = [...s.cartDrawerGroup.order];
            if (typeof index === 'number') order.splice(index, 0, sectionId);
            else order.push(sectionId);
            return {
              ...s,
              cartDrawerGroup: {
                sections: { ...s.cartDrawerGroup.sections, [sectionId]: section },
                order,
              },
            };
          }
          return s;
        });
      });
      return sectionId;
    },

    async removeSection({ group, sectionId }) {
      mutate(`remove ${sectionId}`, () => {
        set((s) => {
          const next = { ...s };
          if (group === 'template') {
            const { [sectionId]: _, ...rest } = next.sections;
            next.sections = rest;
            next.order = next.order.filter((k) => k !== sectionId);
          } else if (group === 'header-group' && next.headerGroup) {
            const { [sectionId]: _, ...rest } = next.headerGroup.sections;
            next.headerGroup = {
              sections: rest,
              order: next.headerGroup.order.filter((k) => k !== sectionId),
            };
          } else if (group === 'footer-group' && next.footerGroup) {
            const { [sectionId]: _, ...rest } = next.footerGroup.sections;
            next.footerGroup = {
              sections: rest,
              order: next.footerGroup.order.filter((k) => k !== sectionId),
            };
          } else if (group === 'cart-drawer-group' && next.cartDrawerGroup) {
            const { [sectionId]: _, ...rest } = next.cartDrawerGroup.sections;
            next.cartDrawerGroup = {
              sections: rest,
              order: next.cartDrawerGroup.order.filter((k) => k !== sectionId),
            };
          }
          if (
            next.selection &&
            'sectionId' in next.selection &&
            next.selection.sectionId === sectionId
          ) {
            next.selection = null;
          }
          return next;
        });
      });
    },

    toggleSectionDisabled({ group, sectionId }) {
      mutate(`toggle ${sectionId}`, () => {
        set((s) => {
          const gd = getGroupData(s, group);
          const current = gd.sections[sectionId];
          if (!current) return s;
          const updated = cloneSection(current);
          updated.disabled = !current.disabled;
          return writeSectionIntoGroup(s, group, sectionId, updated);
        });
      });
    },

    // ---------------------------------------------------------------- blocks
    addBlock({ group, sectionId }, blockType, index) {
      const { metadata } = get();
      const section = getGroupData(get(), group).sections[sectionId];
      if (!section) return null;
      const sectionSchema = metadata?.theme.schemas[section.type];
      const blockSchema = (sectionSchema?.blocks || []).find((b) => b.type === blockType);
      const blockSettings: Record<string, any> = {};
      for (const s of blockSchema?.settings || []) {
        if (s.id && s.default !== undefined) blockSettings[s.id] = s.default;
      }
      const blockId = `b_${Math.random().toString(36).slice(2, 10)}`;

      mutate(`add block ${blockType}`, () => {
        set((s) => {
          const current = getGroupData(s, group).sections[sectionId];
          if (!current) return s;
          const updated = cloneSection(current);
          updated.blocks = {
            ...updated.blocks,
            [blockId]: { type: blockType, settings: blockSettings },
          };
          const order = [...updated.blockOrder];
          if (typeof index === 'number' && index >= 0 && index <= order.length) {
            order.splice(index, 0, blockId);
          } else {
            order.push(blockId);
          }
          updated.blockOrder = order;
          return writeSectionIntoGroup(s, group, sectionId, updated);
        });
      });
      return blockId;
    },

    removeBlock({ group, sectionId, blockId }) {
      mutate(`remove block ${blockId}`, () => {
        set((s) => {
          const current = getGroupData(s, group).sections[sectionId];
          if (!current || !current.blocks[blockId]) return s;
          const updated = cloneSection(current);
          const { [blockId]: _drop, ...rest } = updated.blocks;
          updated.blocks = rest;
          updated.blockOrder = updated.blockOrder.filter((b) => b !== blockId);
          const next = writeSectionIntoGroup(s, group, sectionId, updated);
          if (
            next.selection?.kind === 'block' &&
            next.selection.sectionId === sectionId &&
            next.selection.blockId === blockId
          ) {
            next.selection = { kind: 'section', group, sectionId };
          }
          return next;
        });
      });
    },

    reorderBlocks({ group, sectionId }, newOrder) {
      mutate(`reorder blocks`, () => {
        set((s) => {
          const current = getGroupData(s, group).sections[sectionId];
          if (!current) return s;
          const updated = cloneSection(current);
          updated.blockOrder = [...newOrder];
          return writeSectionIntoGroup(s, group, sectionId, updated);
        });
      });
    },

    toggleBlockDisabled({ group, sectionId, blockId }) {
      mutate(`toggle block ${blockId}`, () => {
        set((s) => {
          const current = getGroupData(s, group).sections[sectionId];
          if (!current || !current.blocks[blockId]) return s;
          const updated = cloneSection(current);
          const block = updated.blocks[blockId] || { type: '', settings: {} };
          const currDisabled = block.settings?.disabled === true;
          block.settings = { ...(block.settings || {}), disabled: !currDisabled };
          updated.blocks = { ...updated.blocks, [blockId]: block };
          return writeSectionIntoGroup(s, group, sectionId, updated);
        });
      });
    },

    // ---------------------------------------------------------- save / drafts
    async save() {
      const state = get();
      const { session, themeBaseline, page } = state;
      if (!session || !themeBaseline) return;

      type Op = { kind: string; run: () => Promise<any> };
      const ops: Op[] = [];

      // --- Theme-wide diffs (global settings, header-group, footer-group) ---
      if (
        JSON.stringify(state.globalSettings) !==
        JSON.stringify(themeBaseline.globalSettings)
      ) {
        ops.push({
          kind: 'global',
          run: () => saveGlobalSettings(session, state.globalSettings),
        });
      }
      pushGroupOps(ops, session, 'header-group', state.headerGroup, themeBaseline.headerGroup);
      pushGroupOps(ops, session, 'footer-group', state.footerGroup, themeBaseline.footerGroup);
      pushGroupOps(ops, session, 'cart-drawer-group', state.cartDrawerGroup, themeBaseline.cartDrawerGroup);

      // --- Per-page template diffs (current page + cached) ---
      /*
        `page` é a chave LÓGICA do estado ('collection', 'product').
        `saveAs` é o NOME do arquivo (sem extensão) que o engine vai
        gravar no S3 — pode ser igual a page ou um suffix
        ('collection.summer-sale') quando existe template customizado
        pra coleção.

        Engine devolve o nome resolvido em `metadata.theme.resolvedTemplate`.
        Se não vier (lojas mais antigas, response em cache de versão
        antiga do engine), cai no `page` — comportamento legado.
      */
      type PageOp = {
        page: string;
        saveAs: string;
        sections: Record<string, SectionMetadata>;
        order: string[];
      };
      const currentSaveAs = state.metadata?.theme?.resolvedTemplate || page;
      const allPages: PageOp[] = [{
        page,
        saveAs: currentSaveAs,
        sections: state.sections,
        order: state.order,
      }];
      for (const [p, slice] of Object.entries(state.pageCache)) {
        if (p === page) continue;
        const saveAs = slice.metadata?.theme?.resolvedTemplate || p;
        allPages.push({
          page: p,
          saveAs,
          sections: slice.sections,
          order: slice.order,
        });
      }

      for (const pg of allPages) {
        const base = state.pageBaselines[pg.page];
        if (!base) continue; // never loaded server baseline — skip
        pushPageOps(ops, session, pg.saveAs, pg, base);
      }

      if (ops.length === 0) {
        set({ saveStatus: { state: 'saved', lastSavedAt: Date.now() } });
        return;
      }

      set({ saveStatus: { state: 'saving', progress: { current: 0, total: ops.length } } });
      try {
        for (let i = 0; i < ops.length; i++) {
          await ops[i].run();
          set({ saveStatus: { state: 'saving', progress: { current: i + 1, total: ops.length } } });
        }
        try { localStorage.removeItem(draftKey(session)); } catch {}

        /*
          CRITICAL FIX: depois do save, fazer reload FULL do servidor.

          Bug que motivou: user reportou "alterei a posição do texto pra
          esquerda, estava centro, cliquei em salvar, dei f5. Na config
          vai mostrar que tá a esquerda mas o que tá renderizado vai ser
          centro".

          Causa raiz (hipótese): após o save, o estado in-memory tinha o
          novo valor mas o iframe ainda mostrava HTML renderizado com o
          valor antigo (live-patch tinha aplicado só parte das mudanças,
          ou tinha alguma divergência entre state e DOM). F5 deveria
          ressincronizar mas em alguns casos o cache de iframe srcdoc
          ou state baselines preservavam algo stale.

          Fix DEFENSIVO: após cada save bem-sucedido, descarta TUDO em
          memória e re-fetcha do servidor com `nocache: true`. Isso:
            - Lê o JSON recém-escrito no S3 (canonical truth)
            - Re-renderiza o HTML do iframe a partir do mesmo state
            - Reseta themeBaseline + pageBaseline pra esse estado
              fresco — diff vira zero, save button desativa, e qualquer
              edição posterior é comparada contra a versão ACABADA DE
              SALVAR (não contra o que estava no boot).

          Custo: 1 request extra por save (~200-500ms). Vale a pena
          pra eliminar qualquer ambiguidade entre "o que tá salvo" e
          "o que tá renderizado".
        */
        await get().reloadPageFromServer();
        set({ saveStatus: { state: 'saved', lastSavedAt: Date.now() } });
      } catch (err: any) {
        set({ saveStatus: { state: 'error', message: err.message || 'Falha ao salvar' } });
      }
    },

    discardChanges() {
      const { themeBaseline, pageBaselines, page, session } = get();
      if (!themeBaseline) return;
      set((s) => ({
        ...s,
        globalSettings: JSON.parse(JSON.stringify(themeBaseline.globalSettings)),
        headerGroup: themeBaseline.headerGroup
          ? JSON.parse(JSON.stringify(themeBaseline.headerGroup))
          : null,
        footerGroup: themeBaseline.footerGroup
          ? JSON.parse(JSON.stringify(themeBaseline.footerGroup))
          : null,
        cartDrawerGroup: themeBaseline.cartDrawerGroup
          ? JSON.parse(JSON.stringify(themeBaseline.cartDrawerGroup))
          : null,
        sections: pageBaselines[page]
          ? JSON.parse(JSON.stringify(pageBaselines[page].sections))
          : {},
        order: pageBaselines[page] ? [...pageBaselines[page].order] : [],
        pageCache: {}, // drop any in-memory edits to other pages
        past: [],
        future: [],
      }));
      if (session) try { localStorage.removeItem(draftKey(session)); } catch {}
    },

    restoreDraft() {
      const { pendingDraft } = get();
      if (!pendingDraft) return;
      const draft = pendingDraft;

      /*
        Captura os baselines do servidor ANTES de aplicar o draft. O
        `reloadPage()` lá embaixo sobrescreve `pageBaselines[page]` com
        o estado pós-render (que já contém o draft), o que zeraria o
        diff — `persistDraft` veria draft == baseline e deletaria o
        localStorage. User pensaria que salvou quando não salvou.

        Preservar o baseline original garante que após restaurar, o
        botão "Salvar" continua ativo e o draft continua sendo persistido
        no localStorage até o user clicar Salvar de fato.
      */
      const savedThemeBaseline = get().themeBaseline
        ? JSON.parse(JSON.stringify(get().themeBaseline))
        : null;
      const savedPageBaselines = JSON.parse(JSON.stringify(get().pageBaselines));

      set((s) => {
        const nextPage = draft.pages[s.page];
        const cache: Record<string, PageSlice> = { ...s.pageCache };
        // For every page in draft other than the current: stash its sections/order
        // into pageCache. Use the existing cached metadata/html if we have it,
        // else synthesize placeholders (will be hydrated when user navigates to that page).
        for (const [p, data] of Object.entries(draft.pages)) {
          if (p === s.page) continue;
          cache[p] = {
            sections: JSON.parse(JSON.stringify(data.sections)),
            order: [...data.order],
            initialHtml: cache[p]?.initialHtml || '',
            metadata: cache[p]?.metadata || s.metadata!,
          };
        }
        return {
          ...s,
          globalSettings: JSON.parse(JSON.stringify(draft.theme.globalSettings)),
          headerGroup: draft.theme.headerGroup
            ? JSON.parse(JSON.stringify(draft.theme.headerGroup))
            : null,
          footerGroup: draft.theme.footerGroup
            ? JSON.parse(JSON.stringify(draft.theme.footerGroup))
            : null,
          cartDrawerGroup: draft.theme.cartDrawerGroup
            ? JSON.parse(JSON.stringify(draft.theme.cartDrawerGroup))
            : null,
          sections: nextPage ? JSON.parse(JSON.stringify(nextPage.sections)) : s.sections,
          order: nextPage ? [...nextPage.order] : s.order,
          pageCache: cache,
          pendingDraft: null,
          past: [],
          future: [],
        };
      });

      /*
        Re-render o iframe com o draft aplicado. `reloadPage` envia
        o estado atual (que acabou de receber o draft) como
        `drafts.files`/`drafts.globalSettings`, então o engine
        renderiza tudo com os valores restaurados.

        Sem isso, o iframe ficaria mostrando os valores do servidor
        (renderizados no boot) enquanto o inspector mostraria os
        valores do draft — exatamente o mismatch que motivou esse
        fix. Live-patch sozinho não cobre 100% das settings (a saga
        do `combo_enabled` provou isso), então re-render full é mais
        seguro.

        Após o reload, RESTAURA os baselines do servidor pra que o
        diff continue sendo válido — vide comentário no início do
        método.
      */
      void get().reloadPage().then(() => {
        set({
          themeBaseline: savedThemeBaseline,
          pageBaselines: savedPageBaselines,
        });
      });
    },

    dismissDraft() {
      const { session } = get();
      set({ pendingDraft: null });
      if (session) try { localStorage.removeItem(draftKey(session)); } catch {}
    },

    setSaveStatus(status) {
      set({ saveStatus: status });
    },

    dismissThemeSync() {
      set({ themeSync: null });
    },

    dismissThemeSyncError() {
      set({ themeSyncError: null });
    },

    // ---------------------------------------------------------------- history
    undo() {
      const { past } = get();
      if (past.length === 0) return;
      const previous = past[past.length - 1];
      const current = takeHistoryEntry(get(), 'current');
      set((s) => ({
        past: s.past.slice(0, -1),
        future: [current, ...s.future],
      }));
      get()._restore(previous);
      persistDraft();
    },

    redo() {
      const { future } = get();
      if (future.length === 0) return;
      const next = future[0];
      const current = takeHistoryEntry(get(), 'current');
      set((s) => ({
        past: [...s.past, current],
        future: s.future.slice(1),
      }));
      get()._restore(next);
      persistDraft();
    },

    _snapshot(label) {
      const snap = takeHistoryEntry(get(), label);
      set((s) => ({
        past: [...s.past, snap].slice(-100),
        future: [],
      }));
    },

    _restore(entry) {
      set({
        sections: entry.sections,
        order: entry.order,
        headerGroup: entry.headerGroup,
        footerGroup: entry.footerGroup,
        globalSettings: entry.globalSettings,
      });
    },
  };
});

// ---------------------------------------------------------------------------
// Save-orchestration helpers (kept outside the store for clarity)
// ---------------------------------------------------------------------------

function pushGroupOps(
  ops: Array<{ kind: string; run: () => Promise<any> }>,
  session: EditorSession,
  group: 'header-group' | 'footer-group' | 'cart-drawer-group',
  cur: GroupData | null,
  base: GroupData | null
) {
  const curSections = cur?.sections || {};
  const baseSections = base?.sections || {};
  const curOrder = cur?.order || [];
  const baseOrder = base?.order || [];
  const ids = new Set([...Object.keys(curSections), ...Object.keys(baseSections)]);

  for (const id of ids) {
    const a = baseSections[id];
    const b = curSections[id];
    if (!a && b) {
      ops.push({
        kind: `add ${group} ${id}`,
        run: () =>
          apiAddSection(session, {
            group,
            sectionId: id,
            section: {
              type: b.type,
              settings: b.settings,
              blocks: b.blocks,
              blockOrder: b.blockOrder,
              disabled: b.disabled,
            },
          }),
      });
    } else if (a && !b) {
      ops.push({
        kind: `remove ${group} ${id}`,
        run: () => apiRemoveSection(session, { group, sectionId: id }),
      });
    } else if (a && b && JSON.stringify(a) !== JSON.stringify(b)) {
      ops.push({
        kind: `update ${group} ${id}`,
        run: () =>
          saveSectionSettings(session, {
            group,
            sectionId: id,
            // type sempre enviado — se por algum motivo a section não
            // estiver persistida no servidor (baseline dessincronizado),
            // o backend cria via upsert em vez de 500.
            type: b.type,
            settings: b.settings,
            blocks: b.blocks,
            blockOrder: b.blockOrder,
            disabled: b.disabled,
          }),
      });
    }
  }

  if (JSON.stringify(curOrder) !== JSON.stringify(baseOrder)) {
    ops.push({
      kind: `order ${group}`,
      run: () => saveSectionOrder(session, { group, order: curOrder }),
    });
  }
}

function pushPageOps(
  ops: Array<{ kind: string; run: () => Promise<any> }>,
  session: EditorSession,
  page: string,
  cur: { sections: Record<string, SectionMetadata>; order: string[] },
  base: PageBaseline
) {
  const ids = new Set([...Object.keys(cur.sections), ...Object.keys(base.sections)]);
  for (const id of ids) {
    const a = base.sections[id];
    const b = cur.sections[id];
    if (!a && b) {
      ops.push({
        kind: `add ${page} ${id}`,
        run: () =>
          apiAddSection(session, {
            group: 'template',
            page,
            sectionId: id,
            section: {
              type: b.type,
              settings: b.settings,
              blocks: b.blocks,
              blockOrder: b.blockOrder,
              disabled: b.disabled,
            },
          }),
      });
    } else if (a && !b) {
      ops.push({
        kind: `remove ${page} ${id}`,
        run: () => apiRemoveSection(session, { group: 'template', page, sectionId: id }),
      });
    } else if (a && b && JSON.stringify(a) !== JSON.stringify(b)) {
      ops.push({
        kind: `update ${page} ${id}`,
        run: () =>
          saveSectionSettings(session, {
            group: 'template',
            page,
            sectionId: id,
            type: b.type,
            settings: b.settings,
            blocks: b.blocks,
            blockOrder: b.blockOrder,
            disabled: b.disabled,
          }),
      });
    }
  }
  if (JSON.stringify(cur.order) !== JSON.stringify(base.order)) {
    ops.push({
      kind: `order ${page}`,
      run: () => saveSectionOrder(session, { group: 'template', page, order: cur.order }),
    });
  }
}

// ---------------------------------------------------------------------------
// Engine-format serialisation
// ---------------------------------------------------------------------------

/**
 * Convert the in-memory group shape (sections + order) into the JSON
 * shape the engine's file reader expects. Two differences:
 *
 *   - Store keys `blockOrder` (camelCase); engine templates carry
 *     `block_order` (snake_case). Everywhere we persist to disk we
 *     already do this rename — the drafts path needs it too.
 *   - Drop `disabled` sections? No — the engine's renderer respects
 *     `disabled` at render time, so we include them as-is.
 *
 * Used when streaming the live section state into the engine as
 * drafts.files during a live-preview reload.
 */
function serialiseGroupForEngine(group: {
  sections: Record<string, SectionMetadata>;
  order: string[];
}): { sections: Record<string, any>; order: string[] } {
  const out: Record<string, any> = {};
  for (const [id, s] of Object.entries(group.sections)) {
    out[id] = {
      type: s.type,
      settings: s.settings,
      blocks: s.blocks,
      block_order: s.blockOrder,
      disabled: s.disabled,
    };
  }
  return { sections: out, order: [...group.order] };
}

// ---------------------------------------------------------------------------
// Hydration helpers
// ---------------------------------------------------------------------------

function takeHistoryEntry(s: EditorState, label: string): HistoryEntry {
  return {
    label,
    sections: JSON.parse(JSON.stringify(s.sections)),
    order: [...s.order],
    headerGroup: s.headerGroup ? JSON.parse(JSON.stringify(s.headerGroup)) : null,
    footerGroup: s.footerGroup ? JSON.parse(JSON.stringify(s.footerGroup)) : null,
    cartDrawerGroup: s.cartDrawerGroup ? JSON.parse(JSON.stringify(s.cartDrawerGroup)) : null,
    globalSettings: { ...s.globalSettings },
  };
}

/**
 * The engine returns `md.theme.settings` as a merged snapshot of the
 * tenant's `current.*` block from settings_data.json. That block
 * carries more than theme settings — it also holds section overrides
 * (`sections`) and page placement metadata (`content_for_index`), plus
 * our own bookkeeping (`_lc_default_version`, `_lc_customized_files`).
 *
 * Copying ALL of that into `globalSettings` meant every editor reload
 * would POST the whole blob back as `drafts.globalSettings`, and the
 * engine would merge those extras onto `current` during render — a
 * recipe for stale-state contamination (e.g. sections block from boot
 * clobbering the fresh persisted state).
 *
 * This strips the known non-setting keys so globalSettings stays
 * strictly the flat "key: value" pairs the schema defines.
 */
const NON_SETTING_KEYS = new Set([
  'sections',
  'content_for_index',
  '_lc_default_version',
  '_lc_customized_files',
]);

function sanitizeGlobalSettings(src: Record<string, any>): Record<string, any> {
  const out: Record<string, any> = {};
  for (const [k, v] of Object.entries(src)) {
    if (NON_SETTING_KEYS.has(k)) continue;
    // Settings schema fields are primitives (string/number/bool) or,
    // at most, a flat object (fonts). Top-level arrays/objects that
    // sneak through (like a raw page-section map) are filtered out
    // so drafts stay small and focused.
    out[k] = v;
  }
  return out;
}

/**
 * Full hydration: replaces current page + theme-wide state. Used only on
 * boot/reload. When `preserveDrafts` is true we skip writing theme-wide
 * state (globalSettings, headerGroup, footerGroup) back from the response
 * — the caller already sent those as drafts, so the server rendered with
 * them, but the store's own values are newer and shouldn't be trampled.
 */
function hydrateFromResponse(
  set: (partial: Partial<EditorState>) => void,
  res: EditorResponse,
  _page: string,
  opts?: { preserveDrafts?: boolean }
) {
  const md = res.metadata;
  const patch: Partial<EditorState> = {
    metadata: md,
    initialHtml: res.html,
    sections: md.theme.sections || {},
    order: md.theme.order || [],
    loading: false,
    error: null,
    past: [],
    future: [],
    saveStatus: { state: 'idle' },
  };
  if (!opts?.preserveDrafts) {
    patch.headerGroup = md.theme.headerGroup;
    patch.footerGroup = md.theme.footerGroup;
    patch.cartDrawerGroup = (md.theme as any).cartDrawerGroup || null;
    patch.globalSettings = sanitizeGlobalSettings(md.theme.settings || {});
  }
  set(patch);
}

/**
 * Page-only hydration: updates sections/order and the page-scoped metadata
 * (schemas, files, initialHtml) but leaves theme-wide state intact so the
 * user's in-progress edits to global settings / header / footer survive
 * the page switch.
 */
function hydratePageFromResponse(
  set: (partial: Partial<EditorState>) => void,
  res: EditorResponse,
  _page: string
) {
  const md = res.metadata;
  set({
    metadata: md,
    initialHtml: res.html,
    sections: md.theme.sections || {},
    order: md.theme.order || [],
    loading: false,
    error: null,
    past: [],
    future: [],
    selection: null,
    saveStatus: { state: 'idle' },
  });
}

function loadDraftFromStorage(session: EditorSession): DraftSnapshot | null {
  try {
    const raw = localStorage.getItem(draftKey(session));
    if (!raw) return null;
    return JSON.parse(raw) as DraftSnapshot;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// External helper: fetch rendered HTML for a single section
// ---------------------------------------------------------------------------

export async function renderSingleSection(
  session: EditorSession,
  sectionId: string,
  section: SectionMetadata,
  group: SectionGroup,
  globalSettings: Record<string, any>,
  opts?: { productId?: string; collectionId?: string }
): Promise<string> {
  const res = await renderSection(session, {
    sectionId,
    sectionConfig: {
      type: section.type,
      settings: section.settings,
      blocks: section.blocks,
      block_order: section.blockOrder,
    },
    productId: opts?.productId,
    collectionId: opts?.collectionId,
    globalSettings,
    groupName: group,
  });
  return res.html;
}
